import React, {useState, useEffect} from "react";
import {Stack, Box, Paper, CircularProgress} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate, useLocation} from "react-router-dom";
import useContainer from "../hooks/useContainer";
import CustomTextField from "../../../core/components/ui/CustomTextfield";
import ContainerTypesDropdownComponent from "../common/container-types-dropdown.component";

const EditContainerComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {isUpdatingContainer, isFetchingContainerTypes, containerTypes, getContainerTypes, viewContainer, 
        containerDetails, updateContainer
    } = useContainer();

    const [containerNumberInput, setContainerNumberInput] = useState("");
    const [selectedContainerType, setSelectedContainerType] = useState(null);

    const containerId = new URLSearchParams(location.search).get("id");
    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");


    useEffect(() => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            const parameters = {
                containerUID: containerId,
                orgUID: organizationId
            };
        
            viewContainer(parameters);
        
        } else {
            const parameters = {
                containerUID: containerId
            };
            
            viewContainer(parameters);
        }
    }, [containerId, storedOrgIdFromRBACForBusiness, orgIdFromUrlForPortEmployee]);

    useEffect(() => {
        if (containerDetails) {
            setContainerNumberInput(containerDetails?.container_number);
            setSelectedContainerType(containerDetails?.type);
        }
    }, [containerDetails])


    const handleContainerTypeChange = (value) => {
        setSelectedContainerType(value);
    }

    const handleTextfieldChange = (e) => {
        setContainerNumberInput(e.target.value);
    }

    const handleBack = () => {
        if (orgIdFromUrlForPortEmployee) {
            navigate(`/organization/view?org_uid=${orgIdFromUrlForPortEmployee}`);

        } else {
            navigate("/containers");
        } 
    }

    const handleEditContainer = () => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;
        const isForm = true;

        if (organizationId) {
            const payload = {
                container_uid: containerDetails?.container_uid,  
                container_number: containerNumberInput,
                type: selectedContainerType,   
                org_uid: organizationId,  
                
                // additional variable to pass so that API will work
                status: containerDetails?.status,
            }
            
            updateContainer(payload, isForm, orgIdFromUrlForPortEmployee);

        } else if (!organizationId) {
            const payload = {
                container_uid: containerDetails?.container_uid,  
                container_number: containerNumberInput,
                type: selectedContainerType,     
                
                // additional variable to pass so that API will work
                status: containerDetails?.status,
            }
            
            updateContainer(payload, isForm);
        }
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Container List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{m: "auto", maxWidth: "1000px"}}>  
                <Box sx={{textAlign: "start", mt: 2}}>
                    <CustomTypography 
                        variant="h3"
                        text={"Edit Container"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />

                    <CustomTypography 
                        variant="subtitle1"
                        text={"Update container for entry"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />
                </Box>

                <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px 40px", mt: 3}}>
                    <Box sx={{textAlign: "start", mb: 5}}>
                        <CustomTypography 
                            variant="h3"
                            text={"Container Details"}
                            customStyles={{
                                marginBottom: 0, 
                                fontSize: 22,
                                fontWeight: 600
                            }}
                        />

                        <CustomTypography 
                            variant="caption"
                            text={"Fill in the details to update container"}
                            customStyles={{
                                marginBottom: 0,
                                fontSize: 14 
                            }}
                        />
                    </Box>

                    <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Container Number"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />
                                <CustomTextField 
                                    placeholder={"Enter container number"}
                                    variant={"outlined"}
                                    value={containerNumberInput}
                                    onChange={(e) => handleTextfieldChange(e)}
                                    fullWidth={true}
                                    multiline={false}
                                    hiddenLabel
                                    readOnly={isUpdatingContainer}
                                />    
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Size & Type"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                                <ContainerTypesDropdownComponent 
                                    value={selectedContainerType}
                                    onChange={handleContainerTypeChange}
                                    fullWidth={true}
                                    size={"medium"}
                                    disabled={isUpdatingContainer}
                                    isFetchingContainerTypes={isFetchingContainerTypes}
                                    containerTypes={containerTypes}
                                    getContainerTypes={getContainerTypes}
                                />      
                            </Box>
                        </Stack>
                    </Stack>   

                    <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 7, justifyContent: "end"}}>
                        <CustomButton 
                            variant={"outlined"}
                            label={"Cancel"}
                            customStyles={{}}
                            onClick={(isUpdatingContainer) ? null : handleBack}
                        />     

                        <CustomButton 
                            variant={"contained"}
                            label={"Update Container"}
                            startIcon={isUpdatingContainer && <CircularProgress size="15px" color="#fff" sx={{mr: "2px"}} />}
                            customStyles={{
                                opacity: (containerNumberInput && selectedContainerType && 
                                    (
                                        containerNumberInput !== containerDetails?.container_number || 
                                        selectedContainerType !== containerDetails?.type
                                    ) && 
                                    !isUpdatingContainer) ? "100%" : "55%",
                                cursor: (containerNumberInput && selectedContainerType && 
                                    (
                                        containerNumberInput !== containerDetails?.container_number || 
                                        selectedContainerType !== containerDetails?.type
                                    ) && 
                                    !isUpdatingContainer) ? "pointer" : "not-allowed !important",
                            }}
                            onClick={
                                (containerNumberInput && selectedContainerType && 
                                    (
                                        containerNumberInput !== containerDetails?.container_number || 
                                        selectedContainerType !== containerDetails?.type
                                    ) &&
                                !isUpdatingContainer) ? handleEditContainer : null
                            }
                        />
                    </Stack>  
                </Paper>
            </Box>
       </>
    )
}

export default EditContainerComponent